/**
 * CALCULATIONS — методы расчёта стоимости и времени услуг.
 *
 * Phase 5 modularization (Sprint 6, 2026-05-07).
 * Извлечено из public/js/app.js (DOMAIN: CALCULATIONS — PER-SERVICE и
 * DOMAIN: CALCULATIONS — TOTALS).
 *
 * Методы добавляются на prototype через attach() — это сохраняет вызовы
 * `this.X` идентично прежнему расположению внутри class body.
 *
 * ⚠️ КРИТИЧЕСКИЙ КОД — формулы согласованы с клиентом и НЕ должны меняться
 * без явного запроса. См. CLAUDE.md "Бизнес-логика расчётов":
 *
 *   1. Базовая стоимость = price × quantity по всем секциям
 *   2. Индивидуальные наценки на каждую услугу отдельно
 *   3. Скрытая наценка = % от базовой стоимости только неисключённых услуг
 *   4. НДС = % от суммы с индивидуальными наценками
 *   5. Клиенту = базовая + индивидуальные + НДС + скрытая
 *
 * Pure-версии этих формул живут в public/js/utils/calc-utils.js
 * (там они принимают state как параметр, для unit-тестирования).
 *
 * @module app/calculations
 */

// i18n helper: returns translation if loaded, else fallback (EN). Safe to call before module loads.
const _t = (k, fb) => {
    const v = window.i18n?.t?.(k);
    return (v && v !== k) ? v : (fb || k);
};

/**
 * Прикрепляет методы расчётов к prototype класса.
 * @param {Function} ClassRef - класс ProfessionalQuoteCalculator
 */
export function attachCalculations(ClassRef) {
    Object.assign(ClassRef.prototype, {
        // ════════════════════════════════════════════════════════════════════
        // ▼ DOMAIN: CALCULATIONS — PER-SERVICE
        // ════════════════════════════════════════════════════════════════════

        calculateServiceTotal(service) {
            // Sprint 19j Issue #3 fix: defensive Number(...) || 0 на все поля.
            // Раньше undefined.markup/price/quantity → NaN propagation в caller.
            // Согласовано с public/js/utils/calc-utils.js#calculateServiceTotal.
            const price = Number(service && service.price) || 0;
            const quantity = Number(service && service.quantity) || 0;
            const markup = Number(service && service.markup) || 0;
            const baseTotal = price * quantity;
            return baseTotal * (1 + markup / 100);
        },

        calculateDuration(startTime, endTime) {
            if (!startTime || !endTime) {
                return '—';
            }

            try {
                const [startHours, startMinutes] = startTime.split(':').map(Number);
                const [endHours, endMinutes] = endTime.split(':').map(Number);

                const startTotalMinutes = startHours * 60 + startMinutes;
                const endTotalMinutes = endHours * 60 + endMinutes;

                let durationMinutes = endTotalMinutes - startTotalMinutes;

                // Handle overnight services
                if (durationMinutes < 0) {
                    durationMinutes += 24 * 60;
                }

                const hours = Math.floor(durationMinutes / 60);
                const minutes = durationMinutes % 60;

                const minLabel = _t('app.calculations.duration_min', 'min');
                const hLabel = _t('app.calculations.duration_h', 'h');
                if (hours === 0) {
                    return `${minutes} ${minLabel}`;
                } else if (minutes === 0) {
                    return `${hours} ${hLabel}`;
                } else {
                    return `${hours} ${hLabel} ${minutes} ${minLabel}`;
                }
            } catch (e) {
                return '—';
            }
        },

        // ════════════════════════════════════════════════════════════════════
        // ▼ DOMAIN: CALCULATIONS — TOTALS
        //   Орчестрация всех секций (services / hotels / flights / otherServices)
        //   и применение наценок/налога согласно бизнес-правилам.
        // ════════════════════════════════════════════════════════════════════

        calculateBaseCost() {
            // Sprint 19j Issue #3 fix: defensive — array может быть undefined,
            // service.price/quantity могут быть undefined у legacy estimates.
            const sumSection = (arr) => (Array.isArray(arr) ? arr : []).reduce((sum, service) => {
                const price = Number(service && service.price) || 0;
                const quantity = Number(service && service.quantity) || 0;
                return sum + (price * quantity);
            }, 0);

            return sumSection(this.state.services)
                 + sumSection(this.state.hotels)
                 + sumSection(this.state.flights)
                 + sumSection(this.state.otherServices);
        },

        calculateTotalProfit() {
            // Sprint 18 fix: эта функция расходилась с updateCalculations() — игнорировала
            // fullProfit услуги (которые добавляют свою всю сумму в прибыль).
            // Теперь логика идентична updateCalculations() в app.js (single source of truth).
            // Sprint 19j Issue #3 fix: defensive Number() — legacy estimates могут не иметь
            // hiddenMarkup или service.price → NaN propagation.
            const allServices = [
                ...(this.state.services || []),
                ...(this.state.hotels || []),
                ...(this.state.flights || []),
                ...(this.state.otherServices || [])
            ];

            // Базовая стоимость только неисключённых услуг для скрытой маржи
            // (исключаем fullProfit и excludeFromMarkup — синхронно с app.js)
            const baseCostForHiddenMarkup = allServices.reduce((sum, service) => {
                if (!service) return sum;
                if (service.fullProfit) return sum;          // fullProfit не учитывается
                if (service.excludeFromMarkup) return sum;
                const price = Number(service.price) || 0;
                const quantity = Number(service.quantity) || 0;
                return sum + (price * quantity);
            }, 0);

            // Индивидуальные наценки — только не-fullProfit услуги
            const individualMarkupAmount = allServices.reduce((sum, service) => {
                if (!service || service.fullProfit) return sum;
                const price = Number(service.price) || 0;
                const quantity = Number(service.quantity) || 0;
                const serviceCost = price * quantity;
                const serviceWithMarkup = this.calculateServiceTotal(service);
                return sum + (serviceWithMarkup - serviceCost);
            }, 0);

            // FullProfit услуги: вся сумма (с markup) идёт в прибыль
            const fullProfitAmount = allServices.reduce((sum, service) => {
                if (!service || !service.fullProfit) return sum;
                return sum + this.calculateServiceTotal(service);
            }, 0);

            const hiddenMarkupRate = Number(this.state && this.state.hiddenMarkup) || 0;
            const hiddenMarkupAmount = baseCostForHiddenMarkup * (hiddenMarkupRate / 100);
            return individualMarkupAmount + hiddenMarkupAmount + fullProfitAmount;
        },

        calculateClientTotal() {
            // Sprint 19j Issue #3 fix: defensive Number() everywhere для NaN-prevention.
            const all = [
                ...(this.state.services || []),
                ...(this.state.hotels || []),
                ...(this.state.flights || []),
                ...(this.state.otherServices || [])
            ];

            const totalWithIndividualMarkup = all.reduce((sum, service) => {
                if (!service) return sum;
                return sum + this.calculateServiceTotal(service);
            }, 0);

            // Базовая стоимость только неисключенных услуг для скрытой маржи
            const baseCostForHiddenMarkup = all.reduce((sum, service) => {
                if (!service || service.excludeFromMarkup) return sum;
                const price = Number(service.price) || 0;
                const quantity = Number(service.quantity) || 0;
                return sum + (price * quantity);
            }, 0);

            const hiddenMarkupRate = Number(this.state && this.state.hiddenMarkup) || 0;
            const taxRate = Number(this.state && this.state.taxRate) || 0;
            const hiddenMarkupAmount = baseCostForHiddenMarkup * (hiddenMarkupRate / 100);
            const taxAmount = totalWithIndividualMarkup * (taxRate / 100);
            return totalWithIndividualMarkup + hiddenMarkupAmount + taxAmount;
        }
    });
}
